import React, { useState } from 'react';
import { TRANSFORMATION_STYLES } from '../../utils/constants';
import StyleThumbnail from '../shared/StyleThumbnail';

const ITEMS_PER_PAGE = 6; // 3 columns x 2 rows
const generationMode = 'transformation';

const StyleSelectionScreen = ({ capturedPhoto, onStyleSelect, onBack }) => {
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  const STYLES = TRANSFORMATION_STYLES;
  const styleKeys = Object.keys(STYLES);
  const totalPages = Math.ceil(styleKeys.length / ITEMS_PER_PAGE);
  const paginatedKeys = styleKeys.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);

  const handleContinue = () => {
    if (!selectedStyle) return;
    onStyleSelect({
      ...STYLES[selectedStyle],
      generationMode
    });
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) setCurrentPage((prev) => prev + 1);
  };

  return (
    <div className="h-screen relative overflow-hidden bg-gradient-to-b from-white via-[#F6FBF9] to-[#E4EFEA]">
      {/* Vertical spine */}
      <div className="forensic-spine" />

      {/* Background layers */}
      <div className="forensic-grid-overlay" />
      <div className="forensic-map-overlay opacity-[0.05]" />
      <div className="forensic-circuit-lines" />

      {/* Geometric blocks */}
      <div className="forensic-block forensic-block-2 opacity-50" />
      <div className="forensic-corner-tl" />

      {/* Main content */}
      <div className="relative z-10 h-full flex flex-col px-12 py-6">

        {/* 1. User Photo Preview - Top center (flex to take available space) */}
        <div className="flex justify-center mb-6 flex-1 min-h-0">
          <div className="h-full aspect-[2/3] rounded-xl overflow-hidden border-4 border-white shadow-xl">
            <img
              src={capturedPhoto}
              alt="Captured preview"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* 2. Choose Your Role Title */}
        <div className="text-center mb-4 flex-shrink-0">
          <h1 className="text-2xl font-bold uppercase tracking-[0.15em] text-[#1A1A1A]">
            Choose Your Role
          </h1>
          <p className="text-base uppercase tracking-[0.2em] text-[#6B7770]">
            Select a Transformation
            <span className="font-arabic mx-2">|</span>
            <span className="font-arabic">اختر التحول</span>
          </p>
        </div>

        {/* 3. Back + Continue Buttons - Centered */}
        <div className="flex gap-4 mb-6 flex-shrink-0 justify-center max-w-2xl mx-auto w-full">
          <button
            onClick={onBack}
            className="forensic-btn-secondary px-8 py-4 text-base flex items-center gap-3"
          >
            <span>←</span>
            <span className="font-title uppercase">Back</span>
            <span className="opacity-60">|</span>
            <span className="font-arabic">رجوع</span>
          </button>
          <button
            onClick={handleContinue}
            disabled={!selectedStyle}
            className={`flex-1 forensic-btn-primary py-4 text-lg flex items-center justify-center gap-3 ${
              !selectedStyle ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {selectedStyle ? (
              <>
                <span className="font-title uppercase">Continue</span>
                <span className="text-white/60">|</span>
                <span className="font-arabic font-bold">متابعة</span>
                <span>→</span>
              </>
            ) : (
              <>
                <span className="font-title uppercase">Select a Style</span>
                <span className="text-white/60">|</span>
                <span className="font-arabic font-bold">اختر نمطاً</span>
              </>
            )}
          </button>
        </div>

        {/* 4. Style Grid - 3 columns, centered (flex to take available space) */}
        <div className="flex-[2] min-h-0 overflow-hidden flex justify-center">
          <div className="grid grid-cols-3 gap-4 h-full w-full">
            {paginatedKeys.map((key) => (
              <StyleThumbnail
                key={key}
                style={STYLES[key]}
                isSelected={selectedStyle === key}
                onSelect={() => setSelectedStyle(key)}
              />
            ))}
          </div>
        </div>

        {/* Next Page Button - Floating */}
        {totalPages > 1 && currentPage < totalPages - 1 && (
          <button
            onClick={handleNextPage}
            className="absolute right-12 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full bg-[#0e734b] text-white flex items-center justify-center text-2xl shadow-lg hover:bg-[#0a5a3a] transition-all z-20"
          >
            ›
          </button>
        )}

        {/* 6. Pagination Dots - Bottom */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 py-3 flex-shrink-0">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i)}
                className={`h-2.5 rounded-full transition-all ${
                  i === currentPage ? 'bg-[#0e734b] w-8' : 'bg-[#D1DDD7] w-2.5 hover:bg-[#0e734b]/50'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StyleSelectionScreen;
